/* eslint-disable func-names */
/* eslint-disable camelcase */
/* eslint-disable react/no-this-in-sfc */
import React, { useState, useEffect, useMemo } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import Button from '../../../../Button';
import FormContainer from '../../../FormContainer';
import AddOrEditPageTabs from '../../../AddOrEditPageTabs';
import Alert from '../../../../Alert';

import renderFormInputs from '../../../../../helpers/renderFormInputs';
import createValidationSchema from '../../../../../forms/utils/createValidationSchema';
import formatServerData from '../../../../../forms/utils/formatServerData';
import useAchScheduleForm from '../../../../../forms/loan/achSchedule';
import LoanService from '../../../../../services/LoanService';
import { useUiContext } from '../../../../../context/UiContext';
import { useServicerStore } from '../../../../../store';
import isAlertVisible from './helpers/isAlertVisible';

const tabs = [
  {
    label: 'ACH Schedule',
    activeOn: 'ACH Settings',
  },
  {
    label: 'Bank Account',
  },
];

export default function AchSchedule({ loan, refetchLoan, readOnly }) {
  const [schedule, setSchedule] = useState([]);

  const { showToast } = useUiContext();

  const { t } = useTranslation();

  const userRole = useServicerStore((state) => state.userRole);
  const isBorrower = userRole === 'borrower';
  const isLender = userRole === 'lender';

  const fields = useMemo(
    () => [
      {
        title: t('AddOrEditPage.AddOrEditLoan.Sections.AchSchedule.Steps'),
        fields: [
          {
            type: 'numberPercentageArray',
            name: 'schedule',
            validation: 'object',
            noInitialValue: true,
            achSchedule: true,
            innerFields: [
              {
                title: t(
                  'common.forms.labels.Specific Day or Before or After Due Day'
                ),
                type: 'select',
                name: 'setting',
                options: [
                  {
                    value: 'specific',
                    label: t('common.forms.options.Specific Day'),
                  },
                  {
                    value: 'offset',
                    label: t('common.forms.options.Before or After Due Day'),
                  },
                ],
                defaultValue: 'offset',
              },
              {
                title: t('common.forms.labels.How much of the payment to pay?'),
                name: 'percent',
                type: 'percentage',
                customValidation: [
                  'is-equal-or-less-than-100',
                  t(
                    'AddOrEditPage.AddOrEditLoan.Sections.AchSchedule.Total scheduled payments for the loan must not exceed 100%'
                  ),
                  () => {
                    const values = schedule?.reduce((acc, curr) => {
                      return acc + +curr.percent;
                    }, 0);

                    return values <= 100;
                  },
                ],
              },
              {
                title: t('common.forms.labels.Before or After Due Day'),
                type: 'select',
                name: 'before_after',
                options: [
                  { value: 'before', label: t('common.forms.options.Before') },
                  { value: 'after', label: t('common.forms.options.After') },
                  { value: 'on', label: t('common.forms.options.On') },
                ],
                defaultValue: 'on',
              },
              {
                title: (vals, id) => {
                  const currentSchedule = vals?.schedule?.[id];

                  if (currentSchedule?.setting === 'specific') {
                    return t('common.forms.labels.Which day?');
                  }

                  if (
                    currentSchedule?.setting === 'offset' &&
                    currentSchedule?.before_after === 'before'
                  ) {
                    return t(
                      'common.forms.labels.How many days before the due date?'
                    );
                  }

                  if (
                    currentSchedule?.setting === 'offset' &&
                    currentSchedule?.before_after === 'after'
                  ) {
                    return t(
                      'common.forms.labels.How many days after the due date?'
                    );
                  }

                  return t('common.forms.labels.Day');
                },
                name: 'day_offset',
                type: 'number',
                defaultValue: 0,
                customValidation: [
                  'max-min',
                  t(
                    'AddOrEditPage.AddOrEditLoan.Sections.AchSchedule.This day would make the payment late'
                  ),
                  function (value) {
                    const { setting, before_after } = this.parent;

                    if (before_after === 'on') {
                      return true;
                    }

                    const minValue = setting === 'offset' ? 0 : 1;

                    if (value < minValue || value > 31) {
                      return false;
                    }

                    switch (true) {
                      case setting === 'offset' && before_after === 'after':
                        return value <= loan?.grace_period_days;
                      case setting === 'specific' && before_after === 'after':
                        return (
                          value <=
                          (loan?.payment_due_day ?? 0) +
                            (loan?.grace_period_days ?? 0)
                        );
                      default:
                        return true;
                    }
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
    [loan?.grace_period_days, loan?.payment_due_day, schedule, t]
  );

  const validationSchema = createValidationSchema(fields);

  const { mutate: updateLoan, isPending } = useMutation({
    mutationFn: ({ formValues }) => {
      return LoanService.updateLoan({
        loanId: loan.id,
        formValues: {
          ...loan,
          ...formValues,
        },
      });
    },
    onSuccess: () => {
      refetchLoan();

      showToast({
        text: 'Loan updated successfully',
        type: 'success',
      });
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text:
          error?.response?.data?.message || t('common.Something went wrong'),
        type: 'error',
      });
    },
  });

  const form = useAchScheduleForm(() => {}, loan);

  const formik = useFormik({
    initialValues: loan
      ? formatServerData(
          {
            schedule: loan.ach_schedule.schedule,
            due_day: loan.payment_due_day,
          },
          fields
        )
      : [],
    onSubmit: (values) => {
      updateLoan({
        formValues: {
          ach_type: form.formik.values.ach_type,
          ach_schedule:
            form.formik.values.ach_type === 'automatic'
              ? {
                  extra_principal:
                    form.formik.values.ach_schedule.extra_principal,
                  schedule: values.schedule.map((sch) => {
                    return {
                      ...sch,
                      day_offset:
                        sch.before_after === 'on' ? 0 : sch.day_offset,
                      percent: sch.percent / 100,
                    };
                  }),
                }
              : {
                  extra_principal: null,
                  schedule: null,
                },
        },
      });
    },
    validationSchema:
      form.formik.values.ach_type === 'automatic' ? validationSchema : null,
    enableReinitialize: true,
  });

  useEffect(() => {
    setSchedule(formik.values.schedule);
  }, [formik.values.schedule]);

  const isNotAuto = form.formik.values.ach_type !== 'automatic';

  return (
    <div>
      {!isBorrower && !isLender && (
        <div className="mb-4 flex justify-between md:items-center flex-col md:flex-row gap-2 h-[37px]">
          <AddOrEditPageTabs tabs={tabs} />
        </div>
      )}
      <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
        {isAlertVisible(
          form.formik?.values?.ach_type,
          formik.values?.schedule
        ) && (
          <div className="mb-5">
            <Alert message="This loan is set to automatic ACH, but the scheduled payments are not set up correctly." />
          </div>
        )}
        <div className="mb-10">
          <FormContainer
            form={form}
            noBillComId
            groupSizeOffset={10}
            disabled={!form.formik.dirty}
            isSubmitting={isPending}
            noSubmit
            readOnly={readOnly}
          />
        </div>
        {!isNotAuto && (
          <div className="mb-8">
            <fieldset>
              <legend className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-6 flex justify-between w-full">
                {t(
                  'AddOrEditPage.AddOrEditLoan.Sections.AchSchedule.Scheduled Payment'
                )}
              </legend>
              <div className="grid gap-5 md:grid-cols-3 xl:grid-cols-4">
                {renderFormInputs({
                  fields: fields[0].fields,
                  formik,
                  prevGroupsSize: '',
                  isVisible: true,
                  tabindex: 0,
                  readOnly,
                })}
              </div>
            </fieldset>
          </div>
        )}
        {isNotAuto && (
          <p className="mb-8">
            {t(
              'AddOrEditPage.AddOrEditLoan.Sections.AchSchedule.ACH is turned off'
            )}
          </p>
        )}
        {!readOnly && (
          <Button
            disabled={false}
            type="submit"
            isSubmitting={isPending}
            onClick={formik.handleSubmit}
          >
            {t('common.Submit')}
          </Button>
        )}
      </div>
    </div>
  );
}
